import { makeAutoObservable } from "mobx";

class ReportingBoundaryStore {
  startYear = null;
  startMonth = null;
  endYear = null;
  endMonth = null;
  selectedFrameworkCodes = [];
  availableFrameworks = [];
  frameworksError = null;
  loading = false;
  saving = false;
  isDirty = false;
  isConfigured = null; // null = unknown, true = has config, false = no config
  minUsedYear = null;
  maxUsedYear = null;
  usedFrameworks = [];

  constructor() {
    makeAutoObservable(this);
  }

  setStartYearMonth = ({ year, month }) => {
    this.startYear = year;
    this.startMonth = month;
    this.isDirty = true;
  };

  setEndYearMonth = ({ year, month }) => {
    this.endYear = year;
    this.endMonth = month;
    this.isDirty = true;
  };

  toggleFramework = (code) => {
    if (this.selectedFrameworkCodes.includes(code)) {
      this.selectedFrameworkCodes = this.selectedFrameworkCodes.filter(
        (c) => c !== code,
      );
    } else {
      this.selectedFrameworkCodes = [...this.selectedFrameworkCodes, code];
    }
    this.isDirty = true;
  };

  removeFramework = (code) => {
    this.selectedFrameworkCodes = this.selectedFrameworkCodes.filter(
      (c) => c !== code,
    );
    this.isDirty = true;
  };

  setSelectedFrameworks = (codes) => {
    this.selectedFrameworkCodes = codes;
    this.isDirty = true;
  };

  setAvailableFrameworks = (frameworks) => {
    this.availableFrameworks = frameworks;
  };

  setFrameworksError = (msg) => {
    this.frameworksError = msg ?? null;
  };

  clearFrameworksError = () => {
    this.frameworksError = null;
  };

  setIsConfigured = (val) => {
    this.isConfigured = val;
  };

  setLoading = (val) => {
    this.loading = val;
  };

  setSaving = (val) => {
    this.saving = val;
  };

  hydrate = ({
    startYear,
    startMonth,
    endYear,
    endMonth,
    frameworkCodes,
    minUsedYear,
    maxUsedYear,
    usedFrameworks,
  }) => {
    this.startYear = startYear ? Number(startYear) : null;
    this.startMonth = startMonth ? Number(startMonth) : null;
    this.endYear = endYear ? Number(endYear) : null;
    this.endMonth = endMonth ? Number(endMonth) : null;
    this.selectedFrameworkCodes = frameworkCodes ?? [];
    this.isDirty = false;
    this.minUsedYear = minUsedYear != null ? Number(minUsedYear) : null;
    this.maxUsedYear = maxUsedYear != null ? Number(maxUsedYear) : null;
    this.usedFrameworks = usedFrameworks ?? [];
  };

  get selectedFrameworks() {
    return this.availableFrameworks.filter((f) =>
      this.selectedFrameworkCodes.includes(f.code),
    );
  }

  /** Minimum year/month the end picker may show (next month after start). */
  get endPickerMin() {
    if (!this.startYear) return { year: 2019, month: 1 };
    if (this.startMonth === 12) return { year: this.startYear + 1, month: 1 };
    return {
      year: this.startYear,
      month: this.startMonth ? this.startMonth + 1 : 1,
    };
  }

  get isFormComplete() {
    return (
      !!this.startYear &&
      !!this.endYear &&
      this.selectedFrameworkCodes.length > 0
    );
  }

  get startYearError() {
    if (!this.startYear) return "Start year & month is required.";
    return null;
  }

  get endYearError() {
    if (!this.endYear) return "End year & month is required.";
    if (this.startYear) {
      if (this.endYear < this.startYear)
        return "End year must be equal to or after start year.";
      if (
        this.endYear === this.startYear &&
        this.startMonth &&
        this.endMonth &&
        this.endMonth <= this.startMonth
      ) {
        return "End month must be after start month when years are the same.";
      }
    }
    return null;
  }

  get hasErrors() {
    return !!this.startYearError || !!this.endYearError;
  }

  get isValid() {
    return this.isFormComplete && !this.hasErrors;
  }

  reset = () => {
    this.startYear = null;
    this.startMonth = null;
    this.endYear = null;
    this.endMonth = null;
    this.selectedFrameworkCodes = [];
    this.isDirty = false;
    this.loading = false;
    this.saving = false;
    this.frameworksError = null;
    // isConfigured is intentionally NOT reset — persists so the intro page
    // can redirect immediately without a re-fetch on subsequent visits
    this.minUsedYear = null;
    this.maxUsedYear = null;
    this.usedFrameworks = [];
  };
}

export const reportingBoundaryStore = new ReportingBoundaryStore();
